// RightPanel.jsx — tabbed Comments / SEBI / Audit panel matching the mockup.
// All three tabs read from the real REST API in db.js.
 
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
 
const initials = (name = '') =>
  name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
const avatarColor = (role) => ({
  founder: '#185FA5', legal: '#534AB7', brlm: '#1D9E75',
}[role] || '#888780');
 
export default function RightPanel({ workspace, chapter, user, editor }) {
  const [tab, setTab] = useState('comments');
  const [comments, setComments] = useState([]);
  const [observations, setObservations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [draft, setDraft] = useState('');
 
  const loadComments = () => api.comments(workspace, chapter).then(setComments).catch(() => {});
  const loadSebi = () => api.sebi(workspace, chapter).then(setObservations).catch(() => {});
  const loadAudit = () => api.audit(workspace, chapter).then(setAudit).catch(() => {});
  const loadRevisions = () => api.revisions(workspace, chapter).then(setRevisions).catch(() => {});
 
  useEffect(() => {
    if (!chapter) return;
    let cancelled = false;
    // Load sequentially (not 4 parallel fetches) to stay within the preview's
    // concurrent-request limits.
    (async () => {
      try { const c = await api.comments(workspace, chapter); if (!cancelled) setComments(c); } catch {}
      try { const r = await api.revisions(workspace, chapter); if (!cancelled) setRevisions(r); } catch {}
      try { const s = await api.sebi(workspace, chapter); if (!cancelled) setObservations(s); } catch {}
      try { const a = await api.audit(workspace, chapter); if (!cancelled) setAudit(a); } catch {}
    })();
    return () => { cancelled = true; };
  }, [workspace, chapter]);
  useEffect(() => {
    if (tab === 'sebi') loadSebi();
    if (tab === 'audit') loadAudit();
    if (tab === 'changes') loadRevisions();
  }, [tab]);
 
  async function addComment() {
    if (!draft.trim() || !editor) return;
    const { from, to } = editor.state.selection;
    const quote = editor.state.doc.textBetween(from, to, ' ') || '(no selection)';
    await api.addComment({
      chapter, workspace, anchorFrom: from, anchorTo: to, quote,
      authorId: user.id, authorName: user.name, authorRole: user.role, body: draft,
    });
    setDraft('');
    loadComments();
  }
 
  async function resolve(id) {
    await api.resolveComment(id);
    loadComments();
  }
 
  return (
    <div className="panel">
      <div className="panel-tabs">
        <button className={`pt-tab ${tab === 'comments' ? 'on' : ''}`} onClick={() => setTab('comments')}>
          Comments <span className="cnt">{comments.length}</span>
        </button>
        <button className={`pt-tab ${tab === 'changes' ? 'on' : ''}`} onClick={() => setTab('changes')}>
          Changes <span className="cnt">{revisions.length}</span>
        </button>
        <button className={`pt-tab ${tab === 'sebi' ? 'on' : ''}`} onClick={() => setTab('sebi')}>
          SEBI <span className="cnt">{observations.length}</span>
        </button>
        <button className={`pt-tab ${tab === 'audit' ? 'on' : ''}`} onClick={() => setTab('audit')}>
          Audit
        </button>
      </div>
 
      {tab === 'comments' && (
        <div className="panel-body">
          <div className="cmt-new">
            <textarea rows={2} value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder="Select text in the document, then comment…" />
            <button onClick={addComment}>Add comment</button>
          </div>
          {comments.length === 0 && <Empty text="No comments yet." />}
          {comments.map((c) => (
            <div key={c.id} className={`cmt ${c.resolved ? 'resolved' : ''}`}>
              <div className="cmt-head">
                <span className="cmt-av" style={{ background: avatarColor(c.author_role) }}>
                  {initials(c.author_name)}
                </span>
                <div>
                  <div className="cmt-name">{c.author_name}</div>
                  <div className="cmt-role">{c.author_role}</div>
                </div>
                {c.resolved
                  ? <span className="resolved-tag" style={{ marginLeft: 'auto' }}>Resolved</span>
                  : <span className="cmt-time">{(c.created_at || '').slice(11, 16)}</span>}
              </div>
              {c.quote && <div className="quote">“{c.quote}”</div>}
              <div className="body">{c.body}</div>
              {!c.resolved && <button className="resolve" onClick={() => resolve(c.id)}>Resolve</button>}
            </div>
          ))}
        </div>
      )}
 
      {tab === 'changes' && (
        <div className="panel-body">
          {revisions.length === 0 && <Empty text="No tracked changes yet. Turn on Track changes and start editing." />}
          {revisions.map((r) => (
            <div key={r.id} className="rev-item">
              <div className="rev-head">
                <span className="cmt-av" style={{ background: avatarColor(r.user_role) }}>
                  {initials(r.user_name)}
                </span>
                <div className="rev-who">{r.user_name}</div>
                <span className={`rev-type rev-${r.type}`}>
                  {r.type === 'deletion' ? 'deleted' : 'inserted'}
                </span>
              </div>
              <div className={`rev-text rev-${r.type}`}>{r.text}</div>
              <div className="rev-meta">
                {r.changed_at ? new Date(r.changed_at).toLocaleString() : r.created_at}
              </div>
              {r.reason ? <div className="rev-reason">Reason: {r.reason}</div> : null}
            </div>
          ))}
        </div>
      )}
 
      {tab === 'sebi' && (
        <div className="panel-body">
          {observations.length === 0 && <Empty text="No SEBI observations logged yet." />}
          {observations.map((o) => (
            <div key={o.id} className="sebi-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span className="sebi-id">{o.ref || `OBS-${o.id}`}</span>
                <span className={`sebi-status ss-${o.status}`} style={{ marginLeft: 'auto' }}>
                  {o.status}
                </span>
              </div>
              <div className="sebi-q">{o.query}</div>
              {o.response
                ? <div className="sebi-meta">Response on file</div>
                : (
                  <button className="sebi-draft-btn"
                    onClick={() => sendPromptSafe(
                      `Draft a SEBI-compliant response to this observation for our DRHP ${chapter}: "${o.query}". Use standard ICDR format.`
                    )}>
                    Draft response ↗
                  </button>
                )}
            </div>
          ))}
        </div>
      )}
 
      {tab === 'audit' && (
        <div className="panel-body">
          {audit.length === 0 && <Empty text="No activity recorded yet." />}
          {audit.map((a) => (
            <div key={a.id} className="audit-item">
              <span className="audit-dot" style={{ background: auditColor(a.action) }} />
              <div>
                <div className="audit-who">
                  {a.user_name} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{a.action}</span>
                </div>
                {a.detail && <div className="audit-act">{a.detail}</div>}
                <div className="audit-time">{a.created_at}{a.ip ? ' · IP logged' : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
 
const Empty = ({ text }) => (
  <div style={{ fontSize: 11, color: 'var(--hint)', padding: '8px 2px' }}>{text}</div>
);
 
const auditColor = (action) => ({
  edit: '#1D9E75', comment: '#534AB7', accept: '#185FA5',
  snapshot: '#BA7517', export: '#185FA5', import: '#BA7517', resolve: '#1D9E75',
}[action] || '#888780');
 
// sendPrompt exists only inside the host chat widget; guard for the standalone app.
function sendPromptSafe(text) {
  if (typeof window !== 'undefined' && typeof window.sendPrompt === 'function') {
    window.sendPrompt(text);
  } else {
    alert('Draft request:\n\n' + text);
  }
}
 